import { useEffect, useRef, useState } from 'react'
import { useLocale } from '../shared/i18n/LocaleContext'
import { useTheme, type ThemeMode } from '../shared/theme/ThemeContext'

export function ThemeSelector({ compact = false }: { compact?: boolean }) {
  const { t } = useLocale()
  const { themeMode, setThemeMode } = useTheme()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const options: { value: ThemeMode; label: string; icon: string }[] = [
    { value: 'System', label: t('user.themeSystem'), icon: '◐' },
    { value: 'Light', label: t('user.themeLight'), icon: '☀' },
    { value: 'Dark', label: t('user.themeDark'), icon: '☾' },
  ]
  const selected = options.find(option => option.value === themeMode) ?? options[0]

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [])

  return <div className={`theme-selector ${compact ? 'compact' : ''}`} ref={rootRef}>
    <button type="button" className="theme-select" aria-label={t('user.theme')}
      aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(current => !current)}>
      <span className="theme-icon" aria-hidden="true">{selected.icon}</span>
      <span className="selector-current-label">{selected.label}</span>
      <span className="language-chevron" aria-hidden="true">⌄</span>
    </button>
    {open && <div className="theme-menu" role="listbox" aria-label={t('user.theme')}>
      {options.map(option => <button type="button" role="option"
        aria-selected={option.value === themeMode}
        className={option.value === themeMode ? 'selected' : undefined}
        key={option.value} onClick={() => { void setThemeMode(option.value); setOpen(false) }}>
        <span className="theme-icon" aria-hidden="true">{option.icon}</span>
        <span>{option.label}</span>
        {option.value === themeMode && <span aria-hidden="true">✓</span>}
      </button>)}
    </div>}
  </div>
}
